// ===========================================
// Shared slot-cache invalidation
// ===========================================
// One helper for "this teacher's real availability just changed" — used by
// BOTH the weekly-availability mutations (useUsers) and the time-off/away
// mutations (useTeacherUnavailability), so the set of slot surfaces can't
// drift between the two. Covers every cache built from teacher availability
// windows + the conflict engine: hybrid clinic slots, make-up slots, and
// extra-lesson grids. (Meet & Greet availability is NOT included here — it
// never reads weekly windows; the time-off hooks add `meetAndGreetKeys.all`
// themselves because away periods DO filter the M&G teacher pool. ⚠️ That only
// started working on 2026-09-02, when the admin availability cache was nested
// under that root — as a root-level literal it was reached by nothing. Note
// the reasoning is availability-shaped: a ROSTER change also moves the M&G
// pool, which is why useInvalidateTeacherRosterCaches covers it separately.)
//
// These are rare admin actions, so root-level invalidation is cheap; the
// alternative is parents seeing slots the backend will 409.

import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { hybridBookingKeys } from './useHybridBooking';
import { bookingKeys } from './useBooking';
import { extraLessonKeys } from './useExtraLessonRequests';
import { lessonKeys } from './useLessons';
import { messageKeys } from './useMessages';

export function useInvalidateSlotCaches() {
  const queryClient = useQueryClient();
  return (extraKeys: QueryKey[] = []) => {
    queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
    queryClient.invalidateQueries({ queryKey: bookingKeys.all });
    queryClient.invalidateQueries({ queryKey: extraLessonKeys.all });
    for (const key of extraKeys) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  };
}

// ===========================================
// Shared teacher-ROSTER invalidation
// ===========================================
// "Who exists / who is eligible just changed" — hiring, deactivating, deleting
// a teacher, or changing which instruments they teach. Distinct from the
// availability concern above: availability changes WHEN a known teacher is
// free; a roster change alters WHICH teachers appear at all.
//
// It wraps useInvalidateSlotCaches (roster changes invalidate the slot surfaces
// too) and adds the teacher-derived caches that live outside those roots.
// Mirrors useInvalidateAvailabilitySurfaces in useTeacherUnavailability.ts —
// compose a wrapper, never widen the shared helper, or every roster key would
// also fire on every time-off mutation.
//
// Regression origin (2026-09-01): a newly hired teacher was missing from the
// admin 1:1 clinic pre-booking dropdown until a hard refresh, because the
// roster mutations invalidated only userKeys. With staleTime 5min and
// refetchOnWindowFocus:false, a stale list never self-heals.
export function useInvalidateTeacherRosterCaches() {
  const queryClient = useQueryClient();
  const invalidateSlotCaches = useInvalidateSlotCaches();
  return () => {
    invalidateSlotCaches();
    // The teacher roster itself, plus its sub-branches (availability,
    // replacement involvement, task summary). Written as a literal rather than
    // importing userKeys: useUsers.ts imports THIS module, and closing that
    // require cycle risks a TDZ error on a const initialised at module scope.
    // Must stay in step with userKeys.teachers() in useUsers.ts.
    queryClient.invalidateQueries({ queryKey: ['users', 'teachers'] });
    // Replacement-teacher eligibility. Deliberately the NARROW prefix, not
    // lessonKeys.all — that root has 16 members incl. parameterised list/
    // occurrences, so invalidating it would storm the calendar and lessons
    // pages on every teacher edit. Dropping the trailing lessonId/weekNumbers
    // prefix-matches every variant (same trick as invalidateReplacementQueries).
    queryClient.invalidateQueries({
      queryKey: [...lessonKeys.all, 'availableReplacementTeachers'],
    });
    // The in-app message recipient list. getRecipients() returns every ACTIVE
    // User in the school for an admin (message.service.ts), and deactivating a
    // teacher also flips User.isActive (teacher.service.ts) — so a roster change
    // moves this list in BOTH directions: a new hire is missing, and a departed
    // teacher stays messageable, until the entry is evicted.
    queryClient.invalidateQueries({ queryKey: messageKeys.recipients() });
    // Meet & Greet: getPublicAvailability filters the host pool on
    // Teacher.isActive + canHostMeetAndGreet, so hiring, deactivating or
    // deleting a teacher moves it. ['meetAndGreets'] is meetAndGreetKeys.all —
    // written as a literal because useMeetAndGreet imports useUsers, which
    // imports THIS module, and closing that require cycle risks a TDZ error.
    // Must stay in step with meetAndGreetKeys.all in useMeetAndGreet.ts.
    queryClient.invalidateQueries({ queryKey: ['meetAndGreets'] });
  };
}
